import { useAuth } from "@clerk/clerk-react";
import { Box, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { fetcherAuth } from "utils/api";

import { BrandsToReviewCount, OutreachAutomationBrand } from "../../types";
import BrandReviewCard from "../BrandReviewCard";

const PREVIEW_COUNT = 3;

interface BrandsToReviewPreviewProps {
  refreshKey?: number;
  hasActiveAutomations: boolean;
}

export default function BrandsToReviewPreview({
  refreshKey,
  hasActiveAutomations,
}: BrandsToReviewPreviewProps) {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();

  const [brands, setBrands] = useState<OutreachAutomationBrand[]>([]);
  const [counts, setCounts] = useState<BrandsToReviewCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  const fetchData = useCallback(async () => {
    if (!currentOrg?.id) return;
    setLoading(true);
    try {
      const [brandsRes, countsRes] = await Promise.all([
        fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/outreach-automations/brands?per_page=${PREVIEW_COUNT}`,
          "GET",
          {},
        ),
        fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/outreach-automations/brands/count`,
          "GET",
          {},
        ),
      ]);
      setBrands(brandsRes.outreachAutomationBrands ?? []);
      setCounts(countsRes);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (refreshKey) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const handleApprove = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/approve-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setCounts((prev) =>
        prev
          ? {
              ...prev,
              total: prev.total - 1,
              approvalRequiredCount: prev.approvalRequiredCount - 1,
            }
          : prev,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  const handleReject = async (brand: OutreachAutomationBrand) => {
    if (!currentOrg?.id) return;
    setUpdatingIds((prev) => new Set(prev).add(brand.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/outreach-automations/${brand.outreachAutomationId}/reject-brands`,
        "POST",
        {},
        { bentoBrandIds: [brand.bentoBrandId] },
      );
      setBrands((prev) => prev.filter((b) => b.id !== brand.id));
      setCounts((prev) =>
        prev
          ? {
              ...prev,
              total: prev.total - 1,
              ...(brand.outreachAutomation.approvalMode === "approval_required"
                ? {
                    approvalRequiredCount: prev.approvalRequiredCount - 1,
                  }
                : { autoScheduleCount: prev.autoScheduleCount - 1 }),
            }
          : prev,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(brand.id);
        return next;
      });
    }
  };

  // Don't render at all if user has no active automations
  if (!hasActiveAutomations) return null;

  const remaining = (counts?.total ?? 0) - brands.length;
  const isEmpty = !loading && brands.length === 0;

  return (
    <Box
      sx={{
        mb: 3,
        border: "2px solid #b1b3f8",
        borderRadius: "10px",
        background:
          "linear-gradient(158deg, rgba(177,179,248,0.2) 0%, rgba(177,179,248,0.1) 100%)",
        p: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "8px",
              backgroundColor: "#b1b3f8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-clipboard-check"
              sx={{ fontSize: 13, color: "white" }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: "24px",
                color: "#014339",
              }}
            >
              Brands Ready to Review
            </Typography>
            {counts && (
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: "16px",
                  color: "rgba(1,67,57,0.7)",
                }}
              >
                {counts.total} brand{counts.total !== 1 ? "s" : ""} discovered
                and waiting for review
              </Typography>
            )}
          </Box>
        </Box>
        {!isEmpty && (
          <Box
            onClick={() => navigate("brands-to-review")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              borderRadius: "8px",
              px: 1.5,
              height: 28,
              backgroundColor: "#014339",
              cursor: "pointer",
              flexShrink: 0,
              "&:hover": { backgroundColor: "#012e27" },
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: "white",
                lineHeight: "16px",
              }}
            >
              View All
            </Typography>
            <Box
              component="span"
              className="fa-solid fa-arrow-right"
              sx={{ fontSize: 10, color: "white" }}
            />
          </Box>
        )}
      </Box>

      {/* Brand cards */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Skeleton variant="rounded" width="100%" height={100} />
          <Skeleton variant="rounded" width="100%" height={100} />
          <Skeleton variant="rounded" width="100%" height={100} />
        </Box>
      ) : isEmpty ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            py: 3,
            px: 2,
            border: "1px dashed rgba(1,67,57,0.15)",
            borderRadius: "10px",
            backgroundColor: "rgba(255,255,255,0.4)",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "rgba(1,67,57,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-check"
              sx={{ fontSize: 16, color: "rgba(1,67,57,0.4)" }}
            />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 14,
              color: "rgba(1,67,57,0.7)",
            }}
          >
            You're all caught up
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: "rgba(1,67,57,0.5)",
              textAlign: "center",
              maxWidth: 340,
            }}
          >
            No brands waiting for review right now. New brands will appear here
            as your automations discover them.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {brands.map((brand) => (
            <BrandReviewCard
              key={brand.id}
              brand={brand}
              variant="preview"
              onApprove={handleApprove}
              onReject={handleReject}
              isUpdating={updatingIds.has(brand.id)}
            />
          ))}
        </Box>
      )}

      {/* "More brands" link */}
      {!loading && remaining > 0 && (
        <Box
          onClick={() => navigate("brands-to-review")}
          sx={{
            mt: 1,
            py: 0.75,
            cursor: "pointer",
            "&:hover": { opacity: 0.7 },
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "#014339",
              textAlign: "center",
              lineHeight: "16px",
            }}
          >
            + {remaining} more brand{remaining !== 1 ? "s" : ""} to review
          </Typography>
        </Box>
      )}

      {/* "See all" link even when remaining <= 0 but has brands */}
      {!loading && remaining <= 0 && brands.length > 0 && (
        <Box
          onClick={() => navigate("brands-to-review")}
          sx={{
            mt: 1,
            py: 0.75,
            cursor: "pointer",
            "&:hover": { opacity: 0.7 },
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: "#014339",
              textAlign: "center",
              lineHeight: "16px",
            }}
          >
            See all brands to review
          </Typography>
        </Box>
      )}
    </Box>
  );
}
